// components/attendance/components/SwitchPersonModal.js
import React, { useState, useEffect } from 'react';
import Modal from '../../../components/ui/modal';
import Button from '../../../components/ui/button';
import Select from '../../../components/ui/select';
import Icon from '../../../components/appIcon';
import Image from '../../../components/appImage';
import axiosInstance from 'utils/axiosInstance';
import Notification from '../../../components/ui/notification';

const BACKEND_URL = import.meta.env.VITE_API_URL;

const SwitchPersonModal = ({ 
  record, 
  onClose, 
  onUpdate 
}) => {
  const [switchOption, setSwitchOption] = useState(''); // 'stranger' or 'existing'
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'info'
  });

  // Fetch employees for dropdown
  useEffect(() => {
    if (switchOption === 'existing') {
      fetchEmployees();
    }
  }, [switchOption]);

  const fetchEmployees = async () => {
    try {
      const response = await axiosInstance.get("/employee/getall");
      console.log("res",response.data.data.employees)
      setEmployees(response.data.data.employees || []);
    } catch (err) {
      console.error("Error fetching employees:", err);
      showNotification("Failed to fetch employees", "error");
    }
  };

  const showNotification = (message, type = 'info') => {
    setNotification({
      show: true,
      message,
      type
    });
  };

  const hideNotification = () => {
    setNotification({
      show: false,
      message: '',
      type: 'info'
    });
  };

  const handleSwitchOptionChange = (option) => {
    setSwitchOption(option);
    setSelectedEmployee('');
  };

  const handleSwitch = async () => {
    if (!switchOption) {
      showNotification("Please select an option", "error");
      return;
    }

    if (switchOption === 'existing' && !selectedEmployee) {
      showNotification("Please select an employee", "error");
      return;
    }

    setIsLoading(true);

    try {
      if (switchOption === 'existing') {
        // Update attendance record with selected employee
        await axiosInstance.put(`/attendance/switch-person/${record.id}`, {
          employeeId: selectedEmployee,
          option: 'existing'
        });
        showNotification("Person switched successfully", "success");
      } else if (switchOption === 'stranger') {
        // Create new unknown employee and assign attendance
        await axiosInstance.put(`/attendance/switch-person/${record.id}`, {
          option: 'unknown'
        });
        showNotification("Marked as unknown successfully", "success");
      }

      // Refresh the data
      onUpdate();
      onClose();
    } catch (err) {
      console.error("Error switching person:", err);
      showNotification(
        err.response?.data?.detail || "Failed to switch person", 
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Transform employees array to match Select component's expected format
  const employeeOptions = employees
    .filter(emp => emp.isUnkown !== true)
    .map(emp => ({
      value: emp.id,
      label: `${emp.first_name} ${emp.last_name} (${emp.employee_id})`
    }));

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Switch Person"
      size="lg"
    >
      <div className="space-y-6 p-8">
        {/* Current Detection */}
        <div className="bg-muted/30 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-foreground mb-2">Current Detection</h3>
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-muted rounded-lg overflow-hidden">
              <Image
                src={`${BACKEND_URL}${record?.captureImage}`}
                alt="Current detection"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">
                {record?.employee?.name || "Unknown"}
              </p>
              <p className="text-sm text-muted-foreground">
                ID: {record?.employee?.id || "N/A"}
              </p>
            </div>
          </div>
        </div>

        {/* Switch Options */}
        <div>
          <h3 className="text-sm font-medium text-foreground mb-3">Select Option</h3>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => handleSwitchOptionChange('stranger')}
              className={`p-4 border rounded-lg text-left transition-colors ${
                switchOption === 'stranger'
                  ? 'border-primary bg-primary/10'
                  : 'border-border hover:bg-muted/50'
              }`}
            >
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="UserX" size={18} />
                <span className="font-medium">Unknown</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Mark as unknown person
              </p>
            </button>
            <button
              onClick={() => handleSwitchOptionChange('existing')}
              className={`p-4 border rounded-lg text-left transition-colors ${
                switchOption === 'existing'
                  ? 'border-primary bg-primary/10'
                  : 'border-border hover:bg-muted/50'
              }`}
            >
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Users" size={18} />
                <span className="font-medium">Existing Employee</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Select from existing employees
              </p>
            </button>
          </div>
        </div>

        {/* Option Content */}
        {switchOption === 'existing' && (
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-foreground">Select Employee</h3>
            <Select
              value={selectedEmployee}
              onChange={(value) => setSelectedEmployee(value)}
              placeholder="Select an employee"
              options={employeeOptions}
            />
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-2 pt-4 border-t">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSwitch}
            disabled={isLoading || !switchOption}
          >
            {isLoading ? 'Processing...' : 'Switch'}
          </Button>
        </div>
      </div>

      {/* Notification */}
      {notification.show && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={hideNotification}
          duration={5000}
        />
      )}
    </Modal>
  );
};

export default SwitchPersonModal;